import React, { useState, useEffect } from "react";
import { db } from "../lib/firebase";
import { doc, getDoc, setDoc } from "firebase/firestore";
import "../styles/card.css";
import Loader from "../components/Loader";
import ChartInfo from "../components/dashboard/ChartInfo";
import { sendCardApplicationNotification } from "../lib/CardEmail";

const MyCard = ({ user }) => {
  const BRAND_NAME = process.env.REACT_APP_BRAND_NAME;
  const SUPPORT_EMAIL = process.env.REACT_APP_SUPPORT_EMAIL;

  // State for loading, error, and submission tracking
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState("");
  const [applicationNumber, setApplicationNumber] = useState("");
  const [submitting, setSubmitting] = useState(false);

  // Form data for the card application
  const [formData, setFormData] = useState({
    cardType: "",
    address: "",
    applicationNumber: "",
  });

  // User-specific data and transaction summary
  const [userData, setUserData] = useState(null);
  const [transactions, setTransactions] = useState([]);

  useEffect(() => {
    // Fetches user data from Firestore and calculates transaction summary
    const fetchUserData = async () => {
      try {
        const userId = user.uid;
        const userDoc = await getDoc(doc(db, "users", userId));
        if (userDoc.exists()) {
          const data = userDoc.data();
          setUserData(data);

          // Process transactions for summary
          let fetchedTransactions = data.transactions || [];
          fetchedTransactions = fetchedTransactions.sort(
            (a, b) => new Date(b.date) - new Date(a.date),
          );

          setTransactions(fetchedTransactions);
        } else {
          setError("User not found.");
        }
      } catch (error) {
        setError("Error fetching user data.");
        console.error("Error fetching user data: ", error);
      } finally {
        setLoading(false);
      }
    };

    if (user && user.uid) {
      fetchUserData();
    }
  }, [user]);

  // Updates the form state based on input
  const handleInputChange = (e) => {
    const { name, value } = e.target;
    setFormData({
      ...formData,
      [name]: value,
    });
  };

  // Deletes the existing card application
  const handleDeleteApplication = async () => {
    try {
      setSubmitting(true);
      await setDoc(
        doc(db, "users", user.uid),
        { ...userData, cardApplication: null },
        { merge: true },
      );
      window.location.reload();
    } catch (error) {
      console.error("Error deleting card application:", error);
    } finally {
      setSubmitting(false);
    }
  };

  // Submits the card application and saves to Firestore
  const handleSubmit = async (e) => {
    e.preventDefault();
    setSubmitting(true);
    try {
      const currentDate = new Date();
      const day = currentDate.getDate();
      const month = currentDate.getMonth() + 1;
      const dateAndMonthSum = day + month;
      const generatedApplicationNumber =
        dateAndMonthSum * userData.accountNumber;

      const updatedFormData = {
        ...formData,
        applicationNumber: generatedApplicationNumber,
        report: "This request is pending internal verification",
        status: "pending",
      };

      await setDoc(doc(db, "users", user.uid), {
        ...userData,
        cardApplication: updatedFormData,
      });

      await sendCardApplicationNotification(
        { ...userData, uid: user.uid },
        updatedFormData,
      );

      setApplicationNumber(generatedApplicationNumber);
      setFormData(updatedFormData);
      window.location.reload();
    } catch (error) {
      console.error("Error submitting card application: ", error);
    } finally {
      setSubmitting(false);
    }
  };

  if (loading) {
    return (
      <div>
        <Loader />
      </div>
    );
  }

  if (error) {
    return <div>{error}</div>;
  }

  return (
    <div className="transactions-container">
      <div className="pager">
        {/* Account Summary */}
        <div>
          <ChartInfo transactions={transactions} />
        </div>
      </div>

      {/* Show application number if submitted */}
      {applicationNumber && (
        <div className="application-info">Application sent!</div>
      )}

      {/* Card Section */}
      <div className="card-section-container">
        <div className="card-display-wrapper">
          <h2 className="hero-title">Available Card</h2>
          <div className="credit-card-preview">
            <div className="card-top">
              <div className="card-logo">{BRAND_NAME}</div>
              <div className="card-chip"></div>
            </div>
            <div className="card-number">
              {userData.accountNumber?.match(/.{1,4}/g)?.join(" ") ||
                "•••• •••• •••• ••••"}
            </div>
            <div className="card-details">
              <div className="card-info">
                <div className="info-row">
                  <span className="label">Cardholder Name</span>
                  <div className="value">
                    {userData.fullName || "YOUR NAME"}
                  </div>
                </div>
                <div className="info-row">
                  <span className="label">Expires</span>
                  <div className="value">03/26</div>
                </div>
              </div>
              <div className="card-brand">Mastercard</div>
            </div>
          </div>
        </div>

        {/* Application Form */}
        <div className="card-form-wrapper">
          {userData.cardApplication?.address ? (
            <div className="existing-application">
              <h2 className="hero-title">Card Application Status</h2>
              <div className="application-box">
                <div className="card-status-section">
                  <div className="card-status-row">
                    <span className="card-status-label">
                      Application Number:
                    </span>
                    <span className="card-status-report">
                      {userData.cardApplication?.applicationNumber} -{" "}
                      {userData.cardApplication?.cardType}
                    </span>
                  </div>
                  <div className="card-status-row">
                    <span className="card-status-label">Billing Address:</span>
                    <span className="card-status-report">
                      {userData.cardApplication?.address}
                    </span>
                  </div>
                  <div className="card-status-row">
                    <span className="card-status-label">Status:</span>
                    <span
                      className={`card-status-badge ${userData.cardApplication?.status === "in-progress" ? "in-progress" : userData.cardApplication?.status}`}
                    >
                      {userData.cardApplication?.status === "in-progress"
                        ? "In Progress"
                        : userData.cardApplication?.status
                            ?.charAt(0)
                            .toUpperCase() +
                          userData.cardApplication?.status?.slice(1)}
                    </span>
                  </div>
                  <div className="card-status-row">
                    <span className="card-status-label">Report:</span>
                    <span className="card-status-report">
                      {userData.cardApplication?.report}
                    </span>
                  </div>
                </div>

                <div>
                  For more information, please send your Application Number to our email {" "}
                  <strong>{SUPPORT_EMAIL}</strong>.
                </div>
              </div>

<button
                onClick={handleDeleteApplication}
                className="delete-button"
                disabled={submitting || userData.cardApplication?.status !== "approved"}
              >
                {submitting ? "Cancelling..." : "Cancel Application"}
              </button>
            </div>
          ) : (
            <div className="new-application">
              <h2 className="hero-title">Apply for a Card</h2>
              <p>
                Securely processed with bank-level encryption. Your information
                is always protected.
              </p>

              <form onSubmit={handleSubmit}>
                <div>
                  <label className="label" htmlFor="cardType">
                    Card Type
                  </label>
                  <select
                    id="cardType"
                    name="cardType"
                    value={formData.cardType}
                    onChange={handleInputChange}
                    className="input-field"
                    required
                  >
                    <option value="">Select Card Type</option>
                    <option value="Credit Card">Credit Card</option>
                    <option value="Debit Card">Debit Card</option>
                  </select>
                </div>
                <div>
                  <label className="label" htmlFor="address">
                    Billing Address
                  </label>
                  <input
                    type="text"
                    className="input-field"
                    id="address"
                    name="address"
                    placeholder="123 Address Street, NY, USA"
                    value={formData.address}
                    onChange={handleInputChange}
                    required
                  />
                </div>
                <button
                  type="submit"
                  className="delete-button"
                  disabled={submitting}
                >
                  {submitting ? "Applying..." : "Apply Now"}
                </button>
              </form>
            </div>
          )}
        </div>
      </div>
    </div>
  );
};

export default MyCard;
